import * as React from 'react';
import { useRef } from 'react';
import MainSection from '../../components/sections/MainSection';
import styled from 'styled-components';
import { breakpoints, sizes, theme } from '../../theme';
import { BodyRegular, Headline5 } from '../../components/Elements';
import LogoutIcon from '../../assets/icons/navigation/LogoutIcon';
import Button from '../../components/buttons/Button';
import { useNavigate } from 'react-router';
import PageInfoIcon from '../../assets/icons/controls/PageInfoIcon';
import FlowerIcon from '../../assets/icons/accessibility/FlowerIcon';
import { AuthContext } from '../../contextProviders/AuthContext';
import { RoleType, UserModel } from '../../models/User';
import MyDetailsSection from './components/MyDetailsSection';
import AccessibilitySection from './components/AccessibilitySection';
import strings from '../../strings/strings.json';
import useGetRequest from '../../hooks/useGetRequest';
import useWindowWidth from '../../hooks/useWindowWidth';
import TabStructureComponent from '../../components/tabs/TabStructureComponent';
import { TabDefinition } from 'components/tabs/Tabs';
import ProfileImageModal from './components/ProfileImageModal';
import Notification, { NotificationType } from '../../components/notifiers/Notification';
import { DOCUMENTS_URL } from '../../lib/_api-helpers';
import EditIcon from 'assets/icons/controls/EditIcon';
import { useLocation } from 'react-router-dom';
import { ImageCircle } from 'components/ImageCircle';
type State = { activeTabId: string };

export default function ProfilePage(): JSX.Element {
    const navigate = useNavigate();
    const {
        data: userDetails,
        errors: userDetailsErrors,
        refetchData: refetchUserDetails,
    } = useGetRequest<UserModel>('users/me/details');
    const { userData, signOutNeve } = React.useContext(AuthContext);
    const { state } = useLocation();
    const [activeTabId] = React.useState((state as State)?.activeTabId);

    const { currentWindowWidth } = useWindowWidth();

    const [imageUploadModalOpen, setImageUploadModalOpen] = React.useState<boolean>(false);
    const [showSavedBio, setShowSavedBio] = React.useState<boolean>(false);

    const imageButtonRef = useRef<HTMLButtonElement | null>(null);

    function closeModal() {
        setImageUploadModalOpen(false);
        if (imageButtonRef.current) {
            imageButtonRef.current.focus();
        }
    }

    if (userDetailsErrors.length > 0) {
        return (
            <Notification
                notificationType={NotificationType.DANGER}
                title={strings.profilePage.userDetailsLoadErrorTitle}
                description={strings.errorMessages.genericDetail}
            />
        );
    }

    const NON_TRAINER_TAB_LIST: TabDefinition[] = [
        {
            id: 'details',
            name: 'My Details',
            icon: PageInfoIcon,
            content: (
                <MyDetailsSection
                    bio={userDetails?.bio ?? ''}
                    refetchUserDetails={refetchUserDetails}
                    showSavedBio={showSavedBio}
                    setShowSavedBio={setShowSavedBio}
                />
            ),
        },
        { id: 'accessibility', name: 'Accessibility', icon: FlowerIcon, content: <AccessibilitySection /> },
    ];

    const FULL_TAB_LIST: TabDefinition[] = [...NON_TRAINER_TAB_LIST];

    const tabs = userData.roles.includes(RoleType.TRAINER) ? FULL_TAB_LIST : NON_TRAINER_TAB_LIST;

    const profileImageFilepath = userDetails?.profileImageS3Filepath
        ? `${DOCUMENTS_URL}/${userDetails.profileImageS3Filepath}`
        : '';

    return (
        <ProfilePageContainer>
            <MainSection>
                <HeaderRow>
                    <NameAndImage>
                        {userDetails && (
                            <>
                                <ButtonContainer
                                    ref={imageButtonRef}
                                    onClick={() => {
                                        setImageUploadModalOpen(true);
                                    }}
                                    aria-label={strings.profilePage.changeProfilePictureAriaLabel}
                                >
                                    <ImageCircle $imageurl={profileImageFilepath}>
                                        <EditIconContainer>
                                            <EditIcon colour={theme.secondaryButtonTextColour} />
                                        </EditIconContainer>
                                    </ImageCircle>
                                </ButtonContainer>
                                <div>
                                    <Headline5>
                                        {userDetails.firstName}&nbsp;{userDetails.lastName}
                                    </Headline5>
                                    {userData?.organisation && <BodyRegular>{userData.organisation.name}</BodyRegular>}
                                </div>
                            </>
                        )}
                    </NameAndImage>
                    <Button
                        title={strings.profilePage.signOutButton}
                        aria={strings.profilePage.signOutButton}
                        onClick={async () => {
                            await signOutNeve();
                            navigate('/');
                        }}
                        icon={currentWindowWidth > parseInt(breakpoints.sm) ? <LogoutIcon /> : undefined}
                    />
                </HeaderRow>
                <TabStructureComponent tabs={tabs} initialActiveTabId={activeTabId} />
            </MainSection>
            {imageUploadModalOpen && (
                <ProfileImageModal
                    closeModal={closeModal}
                    refetchUser={refetchUserDetails}
                    currentProfileImage={profileImageFilepath}
                />
            )}
        </ProfilePageContainer>
    );
}

const ButtonContainer = styled.button`
    cursor: pointer;
    padding: ${sizes.spacingXs};
    background-color: unset;
    border: 2px solid ${theme.cardBackground};
    box-sizing: border-box;
    border-radius: ${sizes.borderRadiusLg};
    &:hover {
        background-color: ${theme.secondaryButtonHoverBackgroundColour};
        border: 2px solid ${theme.secondaryButtonBorderColour};
    }
    &:focus {
        background-color: ${theme.buttonFocusBackgroundColour};
        border: 2px solid ${theme.secondaryButtonBorderColour};
        outline: none;
    }
`;

const EditIconContainer = styled.div`
    top: 42px;
    left: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    border-radius: 50%;
    height: 30px;
    width: 30px;
    z-index: 1;
    background-color: ${theme.cardBackground};
    border: 2px solid ${theme.secondaryButtonTextColour};
    padding: ${sizes.spacingXs};
`;

const ProfilePageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: ${sizes.spacingLg} 0;
    min-height: 82px;

    @media (max-width: ${breakpoints.sm}) {
        gap: ${sizes.spacingMd};
        flex-direction: column-reverse;
        justify-content: flex-start;
        align-items: flex-start;
        min-height: 146px;
    }
`;

const NameAndImage = styled.div`
    display: flex;
    align-items: center;
    gap: ${sizes.spacingMd};
    @media (max-width: ${breakpoints.sm}) {
        gap: ${sizes.spacingSm};
    }
`;
